import type { AiContextValue } from '@/components/focus-queue/ai-context';
import type { AgentConversationPresentation } from './agent-chat-presentation';

/** What the Agent can help with in the area of the app it was opened from. */
export interface AgentAreaGuide {
  /** Plain name of the area, used as page context when the page publishes none. */
  area: string;
  /** Welcome heading for this area; the general heading is used when omitted. */
  heading?: string;
  introduction: string;
  /** "Here I can help you…" list shown under the introduction. */
  helpers: readonly string[];
  /** Starter prompts for this area. */
  questions: readonly string[];
  placeholder: string;
}

type RecordName = string | null;

interface AreaRule {
  match: RegExp;
  /** Record kind whose published label names the record on this page. */
  recordKind?: string;
  build: (name: RecordName) => AgentAreaGuide;
}

const REVIEW_NOTE = 'Anything that would save or change a record is shown to you for review first.';

const quoted = (name: RecordName, fallback: string) => (name ? `“${name}”` : fallback);

const RULES: readonly AreaRule[] = [
  {
    match: /^\/partner-engagement\/accounts\/[^/]+/,
    recordKind: 'account',
    build: name => ({
      area: 'Account',
      heading: 'How can I help with this account?',
      introduction: `You're on the account ${quoted(name, 'page')}. I can use its details, contacts, history and strategic brief to help you work with it.`,
      helpers: ['Summarise the account and recent activity', 'Review its open opportunities and enquiries', 'Find the key contacts and who to speak to next', 'Draft a follow-up email or meeting prep', 'Suggest next steps based on its strategic brief'],
      questions: ['Summarise this account', 'What should I do next with this account?', 'Who are the key contacts here?', 'Prepare me for my next meeting'],
      placeholder: 'Ask about this account…'
    })
  },
  {
    match: /^\/partner-engagement\/accounts\/?$/,
    build: () => ({
      area: 'Accounts',
      heading: 'Which accounts need your attention?',
      introduction: "You're in Accounts. I can help you find accounts and decide which ones need attention.",
      helpers: ['Find accounts by name, industry or location', 'Spot accounts that have gone quiet', 'Highlight important accounts with open work', 'Explain an account before you open it'],
      questions: ['Which accounts need attention?', 'Which important accounts have gone quiet?', 'Find accounts with open opportunities'],
      placeholder: 'Ask about your accounts…'
    })
  },
  {
    match: /^\/partner-engagement\/contacts\/[^/]+/,
    recordKind: 'contact',
    build: name => ({
      area: 'Contact',
      introduction: `You're on the contact ${quoted(name, 'page')}. I can use their details, account, history and brief to help you work with them.`,
      helpers: ['Summarise this person and your recent contact', 'Review their enquiries and opportunities', 'Draft an email or call notes', 'Suggest the best next step with them'],
      questions: ['Summarise this contact', 'What should I do next with this contact?', 'Draft a follow-up email', 'What have we discussed recently?'],
      placeholder: 'Ask about this contact…'
    })
  },
  {
    match: /^\/partner-engagement\/contacts\/?$/,
    build: () => ({
      area: 'Contacts',
      introduction: "You're in Contacts. I can help you find people and see who needs a follow-up.",
      helpers: ['Find contacts by name, account or role', 'Spot contacts waiting on a reply', 'Explain a contact before you open them'],
      questions: ['Who needs a follow-up?', 'Find contacts at a company', 'Which contacts have I not spoken to lately?'],
      placeholder: 'Ask about your contacts…'
    })
  },
  {
    match: /^\/partner-engagement\/opportunities\/[^/]+/,
    recordKind: 'opportunity',
    build: name => ({
      area: 'Opportunity',
      introduction: `You're on the opportunity ${quoted(name, 'page')}. I can help you move it forward.`,
      helpers: ['Summarise where it stands and what is blocking it', 'Suggest the next step to progress it', 'Draft a follow-up or proposal outline', 'Check the account and contacts behind it'],
      questions: ['Summarise this opportunity', 'What is blocking this opportunity?', 'What should I do next to progress it?'],
      placeholder: 'Ask about this opportunity…'
    })
  },
  {
    match: /^\/partner-engagement\/opportunities\/?$/,
    build: () => ({
      area: 'Opportunities',
      introduction: "You're in Opportunities. I can help you review the pipeline and keep deals moving.",
      helpers: ['Find stalled or overdue opportunities', 'Summarise the pipeline by stage', 'Suggest which deals to focus on this week'],
      questions: ['Which opportunities are stalled?', 'Summarise my pipeline', 'Which deals should I focus on this week?'],
      placeholder: 'Ask about your pipeline…'
    })
  },
  {
    match: /^\/(partner-engagement\/enquiries|connect)(\/|$)/,
    recordKind: 'enquiry',
    build: name => ({
      area: 'Enquiries',
      introduction: name ? `You're looking at the enquiry ${quoted(name, '')}. I can help you respond and decide what happens next.` : "You're in Enquiries. I can help you respond quickly so no enquiry is missed.",
      helpers: ['Find enquiries waiting on a response', 'Draft a reply', 'Suggest whether to turn an enquiry into an opportunity', 'Link an enquiry to the right account or contact'],
      questions: ['Which enquiries need a response?', 'Draft a reply to the newest enquiry', 'Which enquiries could become opportunities?'],
      placeholder: 'Ask about your enquiries…'
    })
  },
  {
    match: /^\/partner-engagement\/partners(\/|$)/,
    build: () => ({
      area: 'Partners',
      introduction: "You're in Partners. I can help you understand and grow your partner relationships.",
      helpers: ['Summarise a partner and its recent activity', 'Find partners that need attention', 'Suggest ways to strengthen a relationship'],
      questions: ['Which partners need attention?', 'Summarise my partner relationships'],
      placeholder: 'Ask about your partners…'
    })
  },
  {
    match: /^\/acquisition\/(outlook|email|mailbox-monitoring)(\/|$)/,
    build: () => ({
      area: 'Email',
      introduction: "You're in email. I can help you work through your inbox and connect emails to your records.",
      helpers: ['Find emails that need a reply', 'Draft a reply', 'Spot new leads and people who are not yet contacts', 'Link emails to the right account or contact'],
      questions: ['Which emails need a reply?', 'Are there new leads in my email?', 'Draft a reply to the latest email'],
      placeholder: 'Ask about your email…'
    })
  },
  {
    match: /^\/acquisition\/web-signals(\/|$)/,
    build: () => ({
      area: 'Web signals',
      introduction: "You're in Web signals. I can help you review companies the web monitoring flagged as a possible fit.",
      helpers: ['Explain why a company was flagged', 'Rank the strongest signals', 'Suggest which signals to turn into accounts or opportunities'],
      questions: ['Which web signals look strongest?', 'Which signals should I act on first?'],
      placeholder: 'Ask about web signals…'
    })
  },
  {
    match: /^\/(acquisition\/social|whatsapp)(\/|$)/,
    build: () => ({
      area: 'Social',
      introduction: "You're in social messaging. I can help you respond to messages and spot new leads.",
      helpers: ['Find messages waiting on a reply', 'Draft a reply', 'Spot messages that could become leads'],
      questions: ['Which messages need a reply?', 'Are there new leads in my messages?'],
      placeholder: 'Ask about your social messages…'
    })
  },
  {
    match: /^\/acquisition\/voice(\/|$)/,
    build: () => ({
      area: 'Calls',
      introduction: "You're in Calls. I can help you review calls and follow up.",
      helpers: ['Summarise recent calls', 'Find calls that need a follow-up', 'Draft follow-up notes or emails'],
      questions: ['Summarise my recent calls', 'Which calls need a follow-up?'],
      placeholder: 'Ask about your calls…'
    })
  },
  {
    match: /^\/content-hub(\/|$)/,
    build: () => ({
      area: 'Content Hub',
      introduction: "You're in the Content Hub. I can help you plan, write and review content.",
      helpers: ['Suggest post ideas for your audience', 'Draft or improve a post', 'Review what is scheduled and what is missing'],
      questions: ['Suggest content ideas for this week', 'What is scheduled next?', 'Draft a post about our latest news'],
      placeholder: 'Ask about your content…'
    })
  },
  {
    match: /^\/manage\/(quotes|proposals|tenders|sows|business-cases)(\/|$)/,
    build: () => ({
      area: 'Quotes and tenders',
      introduction: "You're in quotes and tenders. I can help you prepare and track your bids.",
      helpers: ['Find quotes and tenders due soon', 'Summarise a tender’s requirements', 'Draft proposal content'],
      questions: ['What is due soon?', 'Which tenders should we bid on?', 'Summarise open quotes'],
      placeholder: 'Ask about quotes and tenders…'
    })
  },
  {
    match: /^\/(insights|relationship-health|lifecycle|dashboard|acquisition\/(analytics|intelligence))(\/|$)/,
    build: () => ({
      area: 'Insights',
      introduction: "You're in Insights. I can help you understand trends and decide what to act on.",
      helpers: ['Explain what the numbers on this page mean', 'Spot risks and opportunities', 'Suggest the most useful actions to take'],
      questions: ['What stands out on this page?', 'Where are the biggest risks?', 'What should we act on first?'],
      placeholder: 'Ask about these insights…'
    })
  },
  {
    match: /^\/(overview|agent\/briefing|work|next-actions)(\/|$)/,
    build: () => ({
      area: 'Overview',
      introduction: "You're on your overview. I can help you plan your day and make sure nothing is missed.",
      helpers: ['Summarise what needs your attention today', 'Prioritise your queue', 'Prepare the next step on any item'],
      questions: ['What needs my attention today?', 'Help me plan my day', 'What is overdue?'],
      placeholder: 'Ask about your day…'
    })
  },
  {
    match: /^\/(admin|settings)(\/|$)/,
    build: () => ({
      area: 'Administration',
      introduction: "You're in administration. I can explain settings and help you set things up.",
      helpers: ['Explain what a setting does', 'Walk you through setting something up', 'Check who has access to what'],
      questions: ['Explain the settings on this page', 'How do I set this up?'],
      placeholder: 'Ask about these settings…'
    })
  }
];

/**
 * The guide for where the Agent was opened, or null when the page has no
 * specific guide (the Agent then shows its general welcome).
 */
export function agentAreaGuide(pathname: string, context: AiContextValue | null): AgentAreaGuide | null {
  const rule = RULES.find(candidate => candidate.match.test(pathname));
  if (!rule) return null;
  const record = context?.record ?? null;
  const name = rule.recordKind && record?.kind === rule.recordKind && record.label.trim() ? record.label.trim() : null;
  return rule.build(name);
}

/** Welcome wording for the Agent panel built from an area guide. */
export function areaGuidePresentation(guide: AgentAreaGuide): AgentConversationPresentation {
  return {
    ...(guide.heading ? { heading: guide.heading } : {}),
    introduction: guide.introduction,
    guidance: `Here I can help you: ${guide.helpers.join(' · ')}. ${REVIEW_NOTE}`,
    starterQuestions: guide.questions,
    messagePlaceholder: guide.placeholder
  };
}
